import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api/endpoints';
import { useAuth } from '../auth/AuthContext';
import { useToast } from './Toast';
import { Button, ErrorNote, Input, Modal } from './ui';

// The signed-in account changes its own sign-in. Leave the new password blank
// to change only the username.
export function AccountDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const { staff, setProfile } = useAuth();
  const [username, setUsername] = useState(staff?.username ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const nextUsername = username.trim().toLowerCase();
  const usernameChanged = nextUsername !== staff?.username;
  const passwordChanged = newPassword.length > 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (passwordChanged && newPassword !== confirmPassword) {
      setError('The new passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await api.auth.updateMe({
        current_password: currentPassword,
        ...(usernameChanged ? { username: nextUsername } : {}),
        ...(passwordChanged ? { new_password: newPassword } : {}),
      });
      setProfile(updated);
      toast.success(
        usernameChanged
          ? `Saved. Sign in as @${updated.username} from now on.`
          : 'Your password has been changed.',
      );
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="My account" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Input
          label="Username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoCapitalize="none"
          autoComplete="username"
          hint="Letters and numbers only, at least 3 characters."
          required
        />
        <Input
          label="New password"
          type="password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          autoComplete="new-password"
          hint="At least 6 characters. Leave blank to keep your current password."
          minLength={6}
        />
        {passwordChanged && (
          <Input
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
            required
          />
        )}
        <div className="border-t border-slate-800 pt-4">
          <Input
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
            hint="Needed to save any change."
            required
          />
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={busy || (!usernameChanged && !passwordChanged)}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
    </Modal>
  );
}
